import { ref, toRefs, reactive, computed, nextTick, set } from 'vue'
import { isArray, getBinaryValue } from '@/common/js/utils'
import moment from 'moment'
import { Message } from 'element-ui'
import { LIB_FILTER_VALUE } from '@/views/material/library/const.js'
import {
  getMediaAssets,
  materialAnalysisTask,
  materialAnalysisQueryTaskResult,
  addFavMaterial,
  deleteFavMaterial,
  getMediaAssetsTableData,
  CaohuaVideoPoster
} from '@/api/common.model'
import { getAiStrategys } from '@/api/tools.model'
import { MATERIAL_TYPE_ID, MATERIAL_TYPE } from '../consts.js'
import { dataFormatter } from '../utils.js'
import { ANALYSIS_TYPE } from '@/views/creatives/const.js'
import { MEDIAS } from '@/common/js/constants.js'

// Constants
export const IMAGE = MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE]
export const GROUP_IMAGE = MATERIAL_TYPE_ID[MATERIAL_TYPE.GROUP_IMAGE]
export const VIDEO = MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO]
export const META = MATERIAL_TYPE_ID[MATERIAL_TYPE.META]
export const AUDIO = MATERIAL_TYPE_ID[MATERIAL_TYPE.AUDIO]
export const LOGO = MATERIAL_TYPE_ID[MATERIAL_TYPE.LOGO]
export const STICKER = MATERIAL_TYPE_ID[MATERIAL_TYPE.STICKER]
export const POSTER = MATERIAL_TYPE_ID[MATERIAL_TYPE.POSTER]
export const FAV = MATERIAL_TYPE_ID[MATERIAL_TYPE.FAV]
export const IMAGE_AUDIO = MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE_AUDIO]
export const SHOT = MATERIAL_TYPE_ID[MATERIAL_TYPE.SHOT]

export const GRID_LAYOUT = 1
export const TABLE_LAYOUT = 2

/**
 * @typedef {Object} MaterialSelectorProps
 * @property {Object} form
 * @property {boolean} visible
 * @property {number|string} assetType
 * @property {number} videoId
 * @property {number|Array} selectedGameId
 * @property {number} adAccountId
 * @property {number} max
 * @property {Function} beforeSubmit
 */

/**
 * Core logic for material selector
 * @param {MaterialSelectorProps} props
 * @param {Function} emit
 */
export function useGrid(props, emit) {
  // State
  const layoutType = ref(GRID_LAYOUT)
  const pageLoading = ref(false)
  const gridData = ref([])
  const tableData = ref([])
  const selectedRows = ref([])
  const sortParams = ref({})

  const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD')
  const dateTime = ref([yesterday, yesterday])

  const makePager = () => ({
    page_size: 20,
    page_id: 1,
    total: 0
  })

  const gridPager = reactive(makePager())
  const tablePager = reactive(makePager())

  // Computed
  const pager = computed(() => {
    return layoutType.value === GRID_LAYOUT ? gridPager : tablePager
  })

  const selectedIds = computed(() => {
    return selectedRows.value.map((row) => row.id)
  })

  const showTableLayout = computed(() => {
    return props.assetType !== POSTER &&
      props.assetType !== LOGO &&
      props.assetType !== FAV &&
      props.assetType !== IMAGE_AUDIO
  })

  // Helper variables
  let filterTimer = null

  const getGridData = async () => {
    await nextTick()
    const params = buildParams(props, pager)
    pageLoading.value = true

    if (params.type === MATERIAL_TYPE.POSTER) {
      if (props.videoId && typeof props.videoId === 'number') {
        CaohuaVideoPoster({ videoId: props.videoId }).then((data) => {
          if (isArray(data)) {
            gridData.value = dataRefactor(data, MATERIAL_TYPE.POSTER)
            pageLoading.value = false
            gridPager.total = 1
          }
        })
      } else {
        Message.warning('获取封面的视频id不存在')
        pageLoading.value = false
      }
    } else {
      getMediaAssets(params).then((data) => {
        const { list, carousels, page_info } = data
        pageLoading.value = false

        if (carousels?.length) {
          gridData.value = carousels.map((item) => ({
            ...item,
            type: MATERIAL_TYPE.IMAGE_AUDIO,
            width: item.images[0].width,
            height: item.images[0].height,
            name: item.file_name,
            url: item.images[0].url,
            preview_url: item.images[0].url
          }))
          gridPager.total = page_info.total_count
          return
        }

        if (!carousels?.length && !list?.length) {
          gridData.value = []
          gridPager.total = 0
        }

        if (isArray(list)) {
          const _gridData = list.map((g) => ({
            ...g.items[0],
            isFavor: g.isFavor
          }))

          if (params.type === VIDEO) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.VIDEO)
          }
          if (params.type === META) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.VIDEO)
          }
          if (params.type === LOGO) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.LOGO)
          }
          if (params.type === STICKER) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.STICKER)
          }
          if ([IMAGE, 2, 3].includes(params.type)) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.IMAGE)
          }
          if ([GROUP_IMAGE].includes(params.type)) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.GROUP_IMAGE)
          }
          if (params.type === AUDIO) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.AUDIO)
          }
          if (params.type === FAV) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.FAV)
          }
          if (params.type === SHOT) {
            gridData.value = dataRefactor(_gridData, MATERIAL_TYPE.VIDEO)
          }

          gridPager.total = page_info.total_number

          const assetGridElement = document.querySelector('.assets-grid')
          if (assetGridElement) {
            assetGridElement.scrollTop = 0
          }
        }
      }).catch(() => {
        pageLoading.value = false
      })
    }
  }

  const getTableData = async () => {
    pageLoading.value = true
    await nextTick()
    const { order, sort } = sortParams.value
    const [beginTime, endTime] = dateTime.value
    const params = buildParams(props, pager)

    getMediaAssetsTableData({ ...params, beginTime, endTime, sort, order }).then((data) => {
      const { list, page_info } = data

      tableData.value = list?.map((row) => {
        let items = {}
        if (params.type === VIDEO) {
          items = dataFormatter[MATERIAL_TYPE.VIDEO](row.items[0])
        }
        if ([IMAGE, 2, 3].includes(params.type)) {
          items = dataFormatter[MATERIAL_TYPE.IMAGE](row.items[0])
        }
        if ([GROUP_IMAGE].includes(params.type)) {
          items = dataFormatter[MATERIAL_TYPE.GROUP_IMAGE](row.items[0])
        }
        return {
          ...row,
          items
        }
      }) || []

      pageLoading.value = false
      tablePager.total = page_info?.total_number || 0
    }).catch(() => {
      pageLoading.value = false
    })
  }

  const queryDataByLayout = () => {
    clearTimeout(filterTimer)
    filterTimer = setTimeout(() => {
      layoutType.value === GRID_LAYOUT ? getGridData() : getTableData()
    }, 360)
  }

  const queryPage = () => {
    gridPager.page_id = 1
    tablePager.page_id = 1
    queryDataByLayout()
  }

  const onLayoutToggle = (type) => {
    if (pageLoading.value) return
    layoutType.value = type
    queryDataByLayout()
  }

  const onTableSortChange = (e) => {
    sortParams.value = {
      sort: e.prop,
      order: { descending: 'desc', ascending: 'asc' }[e.order]
    }
    queryPage()
  }

  const getGridLayoutClass = (grid) => {
    return grid.width > grid.height ? 'horizontal' : 'vertical'
  }
  const getGridLayoutStyle = (grid) => {
    return { height: grid.width > grid.height ? '280px' : '420px' }
  }

  const setSelectedRows = (rows) => {
    const existRows = selectedRows.value
    rows.forEach((row) => {
      const index = existRows.findIndex((g) => g.id === row.id)
      if (index > -1) {
        existRows[index] = row
      } else {
        existRows.push(row)
      }
    })
    selectedRows.value = existRows
  }

  return {
    layoutType,
    pageLoading,
    gridData,
    tableData,
    selectedRows,
    selectedIds,
    dateTime,
    gridPager,
    tablePager,
    pager,
    showTableLayout,
    queryPage,
    queryDataByLayout,
    onLayoutToggle,
    onTableSortChange,
    getGridLayoutClass,
    getGridLayoutStyle,
    // onSubmit,
    // onCancel,
    setSelectedRows
  }
}

/**
 * @description: 素材收藏
 * @param {*} props
 * @param {*} gridData
 * @param {*} selectedRows
 * @param {*} queryPage
 * @return {*}
 */
export const useMaterialFavorite = (props, gridData, selectedRows, queryPage) => {
  let favRequestGap = false
  const model = reactive({
    favoriteLoading: false
  })
  const selectedIds = computed(() => {
    return selectedRows.value.map((row) => row.id)
  })

  const handleFavChange = (grid, flag) => {
    gridData.value = gridData.value.map((g) => {
      if (g.id === grid.id) {
        g.isFavor = flag ? 0 : 1
        return g
      }
      return g
    })
  }

  const onFavoriteStateChange = (grid) => {
    if (favRequestGap) return
    favRequestGap = true
    const promise = grid.isFavor
      ? deleteFavMaterial({ detailId: grid.id })
      : addFavMaterial({ detailIdList: [grid.id] })

    promise.then((data) => {
      if (data) {
        handleFavChange(grid, !!grid.isFavor)
      }
    }).finally(() => {
      favRequestGap = false
    })
  }

  const onSelectedAddToFavorite = () => {
    if (!selectedIds.value?.length) return
    model.favoriteLoading = true
    addFavMaterial({ detailIdList: selectedIds.value })
      .then((data) => {
        if (data) {
          selectedRows.value.forEach((g) => {
            handleFavChange(g, false)
          })
          if (props.assetType === MATERIAL_TYPE_ID[MATERIAL_TYPE.FAV]) {
            queryPage()
          }
          Message.success('收藏成功')
        }
      })
      .finally(() => {
        model.favoriteLoading = false
      })
  }

  return {
    ...toRefs(model),
    onFavoriteStateChange,
    onSelectedAddToFavorite
  }
}

/**
 * @description: 素材分析
 * @param {*} props
 * @param {*} gridData
 * @param {*} selectedRows
 * @param {*} queryPage
 * @return {*}
 */
export const useMaterialAnalysis = (props, queryDataByLayout) => {
  const model = reactive({
    aiAnalysisConfigModal: false,
    analysisInfoLoading: false,
    analysisInstructionConfigList: [],
    analysisInstructionFormRef: null,
    analysisInfo: {
      grid: {},
      analysisResult: {}
    },
    analysisInstructionForm: {
      instruction: {},
      customInstruction: ''
    },
    selectedSegments: [],
    currGrid: null
  })

  const analysisInstructionFormRules = {
    instruction: [
      {
        required: true,
        validator: (rule, value, callback) => {
          if (!value.title) {
            return callback(new Error('请选择识别指令'))
          }
          callback()
        }
      }
    ],
    customInstruction: [
      { required: true, message: '请填写自定义素材分析配置', trigger: 'change' }
    ]
  }

  const checkedIds = computed(() => {
    return model.selectedSegments.map(item => item.image_id)
  })

  const resetAnalysisInfo = () => {
    model.analysisInfo = {
      grid: {},
      analysisResult: {}
    }
  }

  const getStrategies = async () => {
    const [globalStrategys] = await getAiStrategys()
    const { promptContent: configs } = globalStrategys.strategies.find(s => s.strategyType === 3 && s.promptName === 'Custom_Analysis_Order')
    if (configs) {
      try {
        model.analysisInstructionConfigList = JSON.parse(configs)
      } catch (e) {
        model.analysisInstructionConfigList = []
        console.log('JSON.parse Custom_Analysis_Order configs error output: ', e)
      }
    }
  }
  const onTriggerMaterialAnalysis = (rules) => {
    materialAnalysisTask({
      id: model.currGrid.id,
      type: props.analysisType,
      rules: rules
    }).then((data) => {
      if (data) {
        onAnalysisConfigCancel()
        onQueryMaterialAnalysisResult(model.currGrid)
      }
    })
  }

  const onQueryMaterialAnalysisResult = (grid) => {
    model.currGrid = grid
    model.analysisInfoLoading = true
    materialAnalysisQueryTaskResult({
      id: grid.id,
      type: props.analysisType
    }).then((data) => {
      if (data && JSON.stringify(data) === '{}') {
        if (props.analysisType === ANALYSIS_TYPE.SEGMENT) {
          model.aiAnalysisConfigModal = true
          // 如果没有data信息，清空analysisInfo
          resetAnalysisInfo()
          getStrategies()
        }

        if (props.analysisType === ANALYSIS_TYPE.TOP_MATERIAL) {
          onTriggerMaterialAnalysis(undefined)
        }
      } else {
        const { taskStatus } = data
        const taskResult = data.taskResult && data.taskResult.startsWith('{') ? JSON.parse(data.taskResult) : null
        if (taskResult) {
          if (props.analysisType === ANALYSIS_TYPE.TOP_MATERIAL) {
            taskResult.analysis_logic = taskResult.product_desc
            taskResult.slices = taskResult.slices.map(t => ({ ...t, checked: checkedIds.value.includes(t.image_id) }))
          }
          if (props.analysisType === ANALYSIS_TYPE.SEGMENT) {
            taskResult.slices = taskResult.slices.map(t => ({ ...t, checked: checkedIds.value.includes(t.image_id) }))
          }
        }
        model.analysisInfo = {
          grid,
          analysisResult: {
            ...data,
            taskResult: taskResult
          }
        }
        queryDataByLayout()
      }
    }).finally(() => {
      setTimeout(() => {
        model.analysisInfoLoading = false
      }, 100)
    })
  }

  const onSegmentSelect = (selectedSegment) => {
    const slices = model.analysisInfo.analysisResult.taskResult.slices
    slices.forEach(t => {
      if (t.image_id === selectedSegment.image_id) {
        t.checked = !t.checked
      }
    })

    if (checkedIds.value.includes(selectedSegment.image_id)) {
      model.selectedSegments = model.selectedSegments.filter(item => item.image_id !== selectedSegment.image_id)
    } else {
      model.selectedSegments.push(selectedSegment)
    }

    set(model.analysisInfo.analysisResult.taskResult, 'slices', slices)
  }

  const onSelectedSegmentRemove = (img) => {
    onSegmentSelect(img)
  }

  const onAnalysisConfigConfirm = () => {
    model.analysisInstructionFormRef.validate((valid) => {
      if (valid) {
        const { instruction, customInstruction } = model.analysisInstructionForm
        const rules = [{
          ruleTopic: instruction.title,
          ruleDesc: instruction.title === '自定义' ? customInstruction : instruction.desc
        }]

        onTriggerMaterialAnalysis(rules)
      }
    })
  }
  const onAnalysisConfigCancel = () => {
    model.aiAnalysisConfigModal = false
    model.analysisInstructionForm = {
      instruction: {},
      customInstruction: ''
    }
  }

  return {
    ...toRefs(model),
    resetAnalysisInfo,
    analysisInstructionFormRules,
    onQueryMaterialAnalysisResult,
    onTriggerMaterialAnalysis,
    onSegmentSelect,
    onSelectedSegmentRemove,
    onAnalysisConfigConfirm,
    onAnalysisConfigCancel
  }
}

/**
 * @description: 已选素材操作
 * @param {*} props
 * @param {*} gridData
 * @param {*} tableData
 * @param {*} layoutType
 * @param {*} selectedRows
 * @return {*}
 */
export const useSelectedMaterialActions = (props, gridData, tableData, layoutType, selectedRows) => {
  // Drag and drop state
  const movingIndex = ref(null)
  const futureIndex = ref(null)

  const selectedIds = computed(() => {
    return selectedRows.value.map((row) => row.id)
  })
  const onCurrPageItemsSelectAll = () => {
    const data = layoutType.value === GRID_LAYOUT ? gridData.value : tableData.value.map(row => row.items)
    const newValue = [...selectedRows.value]
    for (let i = 0; i < data.length; i++) {
      const grid = data[i]
      if (selectedIds.value.includes(grid.id)) {
        continue
      } else {
        if (props.max > 0 && props.max <= newValue.length) break
        newValue.push(grid)
      }
    }
    selectedRows.value = newValue
  }

  const onSelectedItemRemove = ({ id }) => {
    selectedRows.value = selectedRows.value.filter((g) => g.id !== id)
  }

  const onSelectedItemDragMove = (e) => {
    const { index, futureIndex: fIndex } = e.draggedContext
    movingIndex.value = index
    futureIndex.value = fIndex
    return false // disable sort
  }

  const onSelectedItemDragEnd = ({ newIndex, oldIndex }) => {
    const futureItem = selectedRows.value[futureIndex.value]
    const movingItem = selectedRows.value[movingIndex.value]
    const _images = [...selectedRows.value]
    _images[futureIndex.value] = movingItem
    _images[movingIndex.value] = futureItem
    selectedRows.value = _images
  }

  return {
    onSelectedItemRemove,
    onCurrPageItemsSelectAll,
    onSelectedItemDragEnd,
    onSelectedItemDragMove
  }
}

/**
 * @description: 视频素材video元素节流显示，控制页面上video元素个数
 * @return {*}
 */
export const useVideoElementDisplay = () => {
  const onGridElementMouseEnter = (grid) => {
    set(grid, 'videoVisible', true)
  }

  const onGridElementMouseLeave = (grid) => {
    set(grid, 'videoVisible', false)
  }

  return {
    onGridElementMouseEnter,
    onGridElementMouseLeave
  }
}

/**
 * @description: 构建查询参数
 * @return {*}
 */
function buildParams(props, pager) {
  const { size, sizes, media_tag, toutiaoLabel, toutiaoLabelExclude, amountType, planType, uploadDateRange, imageStyleList, ...form } = props.form
  const { page_size, page_id } = pager.value
  // 尺寸单选
  const [width, height] = size === '不限' ? ['', ''] : size ? size.split('*')?.map((size) => Number(size)) : []
  // 尺寸多选
  const image_size_conditions = sizes?.map((size) => {
    const [width, height] = size.split('*')?.map(Number)
    return { width, height }
  })

  const consumeStates = getBinaryValue(amountType)
  const planStates = getBinaryValue(planType)
  const [imageBeginTime, imageEndTime] = uploadDateRange || []
  const imageStylesIds = imageStyleList.map((i) => i.slice(-1)).flat()
  const params = {
    ...form,
    page_size,
    page_id,
    type: props.assetType,
    // 媒体有消耗，无消耗
    hasAmountType: consumeStates[LIB_FILTER_VALUE.CONSUMED],
    noAmountType: consumeStates[LIB_FILTER_VALUE.UN_CONSUMED],
    createPlanType: planStates[LIB_FILTER_VALUE.USED],
    noPlanType: planStates[LIB_FILTER_VALUE.UN_USED]
  }

  if (imageStylesIds?.length) {
    params.imageStyleStr = imageStylesIds.join(',')
  }

  if (imageBeginTime && imageEndTime) {
    params.imageBeginTime = imageBeginTime
    params.imageEndTime = imageEndTime
  }

  if (media_tag) {
    params.tagId = media_tag
  }

  if (form.media_id && +form.media_id === MEDIAS.TOUTIAO) {
    params.toutiaoLabel = toutiaoLabel.reduce((sum, cVal) => sum + Number(cVal), 0)
    params.toutiaoLabelExclude = toutiaoLabelExclude.reduce((sum, cVal) => sum + Number(cVal), 0)
  }

  if (props.selectedGameId) {
    params.select_game_id = props.selectedGameId
  }

  if (image_size_conditions?.length) {
    params.image_size_conditions = JSON.stringify(image_size_conditions)
  } else {
    params.width = width
    params.height = height
  }

  if (props.assetType === IMAGE_AUDIO) {
    params.ad_account_id = props.adAccountId
  }

  if ([AUDIO, GROUP_IMAGE].includes(props.assetType)) {
    delete params.width
    delete params.height
    props.form.size = ''
  }

  return params
}

function dataRefactor(data, mediaType) {
  return data.map((row) => dataFormatter[mediaType](row))
}
